// HeroCard.jsx — The featured player. The hero of the screen.
import { useRef, useState, useEffect } from 'react';
import { getRankImage, getRoleImage } from '../common/consts';
import { useAuction } from './AuctionContext';
import './HeroCard.css';

const TIER_COLORS = {
  platinum: '#9BE8E0', diamond: '#F472B6',
  ascendant: '#3FE07F', immortal: '#FF5277', radiant: '#FFD84D',
};
const ROLE_COLORS = {
  duelist: '#FF4655', initiator: '#32F5D2',
  controller: '#A78BFA', sentinel: '#FACC15', flex: '#94A3B8',
};
const tc = (t) => TIER_COLORS[(t||'').toLowerCase()] || '#32F5D2';
const rc = (r) => ROLE_COLORS[(r||'').toLowerCase()] || '#94A3B8';

export default function HeroCard() {
  const { player, bid, stage } = useAuction();
  const isReveal = stage === 'reveal';
  const prevBid = useRef(bid);
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    if (bid !== prevBid.current && bid > 0) {
      prevBid.current = bid;
      setPulsing(true);
      const t = setTimeout(() => setPulsing(false), 400);
      return () => clearTimeout(t);
    }
  }, [bid]);

  if (!player) return null;

  const tier = tc(player.rank_tier);
  const role = rc(player.role);
  const val  = bid > 0 ? bid : player.base_price;

  const pricingTier = (player.rank_tier || '').toLowerCase();
  const auctionRank = (() => {
    if (player.peak_rank && player.peak_rank.toLowerCase().includes(pricingTier)) return player.peak_rank.toUpperCase();
    if (player.cur_rank && player.cur_rank.toLowerCase().includes(pricingTier)) return player.cur_rank.toUpperCase();
    return pricingTier.toUpperCase();
  })();

  const cells = [
    { label: 'PEAK RANK',      val: player.peak_rank,  img: getRankImage(player.peak_rank) },
    { label: 'AUCTION RANK',   val: auctionRank,       img: getRankImage(player.rank_tier) },
    { label: 'PRIMARY ROLE',   val: player.role,       img: getRoleImage(player.role),    color: role },
    { label: 'SECONDARY ROLE', val: player.sec_role,   img: getRoleImage(player.sec_role) },
  ].filter(c => c.val);

  return (
    <div
      className="hc"
      style={{ '--card-tier': tier, '--card-role': role }}
    >
      <div className="hc-texture" aria-hidden="true" />
      <div className="hc-scanner" aria-hidden="true" />
      <span className="hc-led hc-led-tl" /><span className="hc-led hc-led-tr" />
      <span className="hc-led hc-led-bl" /><span className="hc-led hc-led-br" />

      {/* Avatar — photo if available, initial letter fallback */}
      <div className="hc-initial-wrap">
        {player.photo ? (
          <div className="hc-photo-wrap">
            <img
              src={player.photo}
              alt={player.game_name}
              className="hc-photo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hc-initial" style={{ display: 'none' }}>
              {(player.game_name || '?')[0].toUpperCase()}
            </div>
          </div>
        ) : (
          <div className="hc-initial">
            {(player.game_name || '?')[0].toUpperCase()}
          </div>
        )}
        <div className="hc-initial-glow"
          style={{ background: `radial-gradient(ellipse, ${tier}30 0%, transparent 70%)` }}
        />
      </div>

      <h1 className={`hc-name ${isReveal ? 'hc-reveal-name' : ''}`}>{player.game_name}</h1>
      {player.valo_id && (
        <div className="hc-valoid" style={{ color: tier }}>{player.valo_id}</div>
      )}
      {(player.city || player.state) && (
        <div className="hc-location">
          {[player.city, player.state].filter(Boolean).join(', ')}
        </div>
      )}

      {/* Info grid */}
      <div className={`hc-grid ${isReveal ? 'hc-reveal-grid' : ''}`}>
        {cells.map(c => (
          <div key={c.label} className="hc-cell">
            {c.img && <img src={c.img} alt="" className="hc-cell-icon" />}
            <div>
              <div className="hc-cell-label">{c.label}</div>
              <div className="hc-cell-val" style={{ color: c.color }}>{c.val}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="hc-divider" />

      {/* Prices */}
      <div className="hc-prices">
        <div className="hc-base">
          <span className="hc-price-label">BASE POINTS</span>
          <span className="hc-base-num">{player.base_price}<small> PTS</small></span>
        </div>
        <div className={`hc-bid ${pulsing ? 'hc-bid-pulse' : ''}`}
          style={{ boxShadow: pulsing ? `0 0 24px ${tier}44` : `0 0 10px ${tier}18` }}
        >
          <span className="hc-price-label">CURRENT BID</span>
          <span className="hc-bid-num" style={{ color: tier }}>
            {val}<small> PTS</small>
          </span>
        </div>
      </div>

      {player.is_unsold_round && <div className="hc-unsold">UNSOLD ROUND</div>}
    </div>
  );
}